import React, { useEffect, useState } from "react";
import { fetchSongsV2, deleteSongV2, SongV2 } from "./songV2Api";
import { Link } from "react-router-dom";
import styles from "./SongV2Shared.module.css";
import {
  Music,
  Edit,
  Trash2,
  Search,
  Clock,
  User,
  Volume2,
  Zap,
  AlertCircle,
  RefreshCw,
  Lock,
} from "lucide-react";

export default function SongV2List() {
  const [songs, setSongs] = useState<SongV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredSongs, setFilteredSongs] = useState<SongV2[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadSongs();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = songs.filter(
        (song) =>
          song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          song.artist.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSongs(filtered);
    } else {
      setFilteredSongs(songs);
    }
  }, [searchTerm, songs]);

  const loadSongs = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchSongsV2({ limit: 100 });
      console.log("SongsV2 API Response:", response);
      // Backend response: { success, message, data: [...], total, limit, offset }
      // fetchSongsV2 already returns PaginatedResponse with data array
      setSongs(response.data || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error("Failed to load songs:", error);
      setError(
        error.response?.data?.message || "Şarkılar yüklenirken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (
      window.confirm(`"${title}" şarkısını silmek istediğinize emin misiniz?`)
    ) {
      try {
        await deleteSongV2(id);
        setSongs(songs.filter((song) => song.songv2_id !== id));
        console.log(`Song deleted: ${title}`);
      } catch (error: any) {
        console.error("Delete failed:", error);
        alert("Şarkı silinirken hata oluştu. Lütfen tekrar deneyin.");
      }
    }
  };

  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getTotalDuration = () => {
    return filteredSongs.reduce(
      (total, song) => total + (song.duration_ms || 0),
      0
    );
  };

  const getAverageBpm = () => {
    if (filteredSongs.length === 0) return 0;
    const totalBpm = filteredSongs.reduce(
      (total, song) => total + (song.bpm || 0),
      0
    );
    return Math.round(totalBpm / filteredSongs.length);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <span>Şarkılar yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardBody} style={{ textAlign: "center" }}>
            <AlertCircle
              size={48}
              style={{ color: "#ef4444", marginBottom: "1rem" }}
            />
            <h3 style={{ color: "white", marginBottom: "0.5rem" }}>
              Hata Oluştu
            </h3>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.8)",
                marginBottom: "1.5rem",
              }}
            >
              {error}
            </p>
            <button onClick={loadSongs} className={styles.button}>
              <RefreshCw size={16} />
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <Music className={styles.titleIcon} />
            SongsV2 Yönetimi
          </h1>
          <p className={styles.subtitle}>
            Şarkı koleksiyonunuzu görüntüleyin ve düzenleyin
          </p>
        </div>
      </div>

      {/* Search */}
      <div className={styles.searchSection}>
        <div className={styles.searchInputWrapper}>
          <Search className={styles.searchIcon} size={20} />
          <input
            type="text"
            placeholder="Şarkı veya sanatçı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#3b82f6" }}>
            <Music size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{filteredSongs.length}</h3>
            <p>Toplam Şarkı</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#10b981" }}>
            <Clock size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{formatDuration(getTotalDuration())}</h3>
            <p>Toplam Süre</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#8b5cf6" }}>
            <Zap size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{getAverageBpm()}</h3>
            <p>Ortalama BPM</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#f59e0b" }}>
            <User size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>
              {Array.from(new Set(filteredSongs.map((s) => s.artist))).length}
            </h3>
            <p>Benzersiz Sanatçı</p>
          </div>
        </div>
      </div>

      {/* Songs List */}
      {filteredSongs.length === 0 ? (
        <div className={styles.card}>
          <div className={styles.emptyState}>
            <Music size={48} style={{ opacity: 0.3 }} />
            <p>
              {searchTerm
                ? "Arama kriterlerine uygun şarkı bulunamadı"
                : "Henüz şarkı eklenmemiş"}
            </p>
          </div>
        </div>
      ) : (
        <div className={styles.contentGrid}>
          {filteredSongs.map((song) => (
            <div key={song.songv2_id} className={styles.card}>
              <div className={styles.cardBody}>
                <div className={styles.songHeader}>
                  <div className={styles.songInfo}>
                    <h3 className={styles.songTitle}>{song.title}</h3>
                    <p className={styles.songArtist}>
                      <User size={14} />
                      {song.artist}
                    </p>
                  </div>
                  <div className={styles.songActions}>
                    <Link
                      to={`/songsv2/edit/${song.songv2_id}`}
                      className={styles.actionButton}
                      style={{ background: "#3b82f6" }}
                      title="Düzenle"
                    >
                      <Edit size={16} />
                    </Link>
                    <button
                      onClick={() => handleDelete(song.songv2_id, song.title)}
                      className={styles.actionButton}
                      style={{ background: "#ef4444" }}
                      title="Sil"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className={styles.songMeta}>
                  <div className={styles.metaItem}>
                    <Clock size={14} />
                    <span>{formatDuration(song.duration_ms || 0)}</span>
                  </div>

                  {song.bpm && (
                    <div className={styles.metaItem}>
                      <Zap size={14} />
                      <span>{song.bpm} BPM</span>
                    </div>
                  )}

                  {song.ts && (
                    <div className={styles.metaItem}>
                      <Volume2 size={14} />
                      <span>{song.ts}</span>
                    </div>
                  )}
                </div>

                <div className={styles.songFooter}>
                  <span className={styles.songId}>
                    #{song.songv2_id.substring(0, 8)}...
                  </span>

                  {song.is_locked && (
                    <span
                      className={styles.lockedBadge}
                      style={{
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.2)",
                        color: "#ef4444",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                      }}
                    >
                      <Lock size={12} />
                      Kilitli
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
